import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { TbDeviceHeartMonitor, TbEye, TbShieldLock } from 'react-icons/tb';

const SectionWrapper = styled.section`
  padding: 8rem 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.elevated}50; // Semi-transparent based on theme
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;
  
  @media (max-width: 1024px) {
    padding: 0 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 2.5rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const FeatureCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary.main}15;
  color: ${({ theme }) => theme.colors.primary.main};
  border-radius: 12px;
  font-size: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1.25rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FeatureDescription = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const features = [
    {
        icon: <TbDeviceHeartMonitor />,
        title: "Continuous Monitoring",
        description: "Real-time vital sign tracking without physical contact, ensuring 24/7 safety and peace of mind for patients and caregivers."
    },
    {
        icon: <TbEye />,
        title: "Privacy First",
        description: "Utilizes low-resolution thermal sensors that preserve anonymity while delivering high-fidelity physiological data."
    },
    {
        icon: <TbShieldLock />,
        title: "Secure & Reliable",
        description: "Enterprise-grade security encryption with robust fallback systems designed for critical healthcare environments."
    }
];

const FeaturesSection = () => {
    return (
        <SectionWrapper>
            <Container>
                <SectionHeader>
                    <SectionTitle>Advanced Detection Capabilities</SectionTitle>
                    <SectionSubtitle>
                        Our technology combines state-of-the-art hardware with proprietary AI algorithms to deliver unprecedented insights.
                    </SectionSubtitle>
                </SectionHeader>

                <Grid>
                    {features.map((feature, index) => (
                        <FeatureCard
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <IconWrapper>{feature.icon}</IconWrapper>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureCard>
                    ))}
                </Grid>
            </Container>
        </SectionWrapper>
    );
};

export default FeaturesSection;
